/**
 * The download row, decided from `availability()` rather than from a hardcoded
 * format list.
 *
 * Three outcomes, and the whole point of the file is that they are THREE and
 * not two:
 *
 *   offer  — a writer exists and it costs nothing. Just a download button.
 *   warn   — a writer exists, something is lost, the file is still correct.
 *            Download button PLUS what it costs. The emoji-in-PDF case.
 *   refuse — a writer exists and the write would be wrong. NO download button.
 *            The Polish/CJK-in-PDF case: a PDF of tofu boxes is not a subtitle
 *            file, and handing it over with a warning beside it makes the
 *            warning the user's problem to evaluate.
 *
 * A format with no writer is not a fourth state — it is ABSENT. Eight of the
 * twenty formats carry a capability row and no writer, and CONVERT's
 * `availability()` reports that as `writable: false` precisely so this row can
 * leave them out. Rendering them disabled would be worse than useless: on a
 * converter site a greyed-out format reads as "pay to unlock", which is the
 * exact impression this product exists to not give.
 *
 * PURE, and in this file rather than in the page, because every branch above is
 * a rule with a right answer and none of them need a DOM to be checked.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ ⚠ NOT YET REACHABLE. `availability()` is exported from                   │
 * │ `src/core/registry/pipeline.ts` and takes a `SubtitleDocument` — which    │
 * │ lives in the worker. The main thread holds a `DocumentId`, and            │
 * │ `src/core/protocol.ts` has no `availability` message, so the UI cannot    │
 * │ call it. This module is written against the shape and tested against it;  │
 * │ wiring is one `client.request('availability', …)` the day it exists.      │
 * │ Escalated in comms/UI.md — third time a shipped default has been          │
 * │ unreachable from its consumer.                                            │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

import type {
  LossCode,
  LossEntry,
  WriteFormat,
  WriteOptions,
} from '../../../../../contracts/cue-model.ts';
import { renderState } from './states.ts';

/** The subset of CONVERT's `FormatAvailability` this row reads. */
export interface Availability {
  readonly format: WriteFormat;
  readonly writable: boolean;
  readonly loss: readonly LossEntry[];
  readonly blocking: boolean;
}

export type RowKind = 'offer' | 'warn' | 'refuse';

export interface LossSummary {
  readonly code: LossCode;
  /** Aggregated across entries sharing a code — one line, not 412. */
  readonly count: number;
  /** The feature's own `detail` strings, deduplicated, in arrival order. */
  readonly details: readonly string[];
  readonly blocking: boolean;
}

export interface DownloadChoice {
  readonly format: WriteFormat;
  readonly kind: RowKind;
  readonly loss: readonly LossSummary[];
}

/**
 * Group a writer's loss report into one line per cause.
 *
 * `LossEntry.count` is already aggregated by the producer ("412 karaoke
 * syllables" is one entry), but a projection and a precheck can each report the
 * same code, and two entries saying `karaoke-dropped` at the user is one fact
 * said twice.
 *
 * `info` entries are left out. They record a change the file can carry without
 * anything being lost to the reader: times rounded to a format's centisecond or
 * frame grid (`precision-reduced`, `frame-quantized`, src/core/project/
 * project.ts). Counting them turned every ASS or frame-based download into a
 * warning, which teaches people to ignore the warnings that matter.
 */
export function summarizeLoss(loss: readonly LossEntry[]): readonly LossSummary[] {
  const byCode = new Map<LossCode, { count: number; details: string[]; blocking: boolean }>();
  for (const entry of loss) {
    if (entry.severity === 'info') continue;
    const existing = byCode.get(entry.code);
    if (existing === undefined) {
      byCode.set(entry.code, {
        count: entry.count,
        details: entry.detail === '' ? [] : [entry.detail],
        blocking: entry.severity === 'error',
      });
      continue;
    }
    existing.count += entry.count;
    existing.blocking ||= entry.severity === 'error';
    if (entry.detail !== '' && !existing.details.includes(entry.detail)) {
      existing.details.push(entry.detail);
    }
  }
  return [...byCode].map(([code, v]) => ({
    code,
    count: v.count,
    details: v.details,
    blocking: v.blocking,
  }));
}

/**
 * What the row may show, in the order the caller asked for.
 *
 * Unwritable formats are dropped rather than marked — see the header. Use
 * `unavailable()` if the page wants to account for them somewhere calmer.
 */
export function downloadChoices(available: readonly Availability[]): readonly DownloadChoice[] {
  const choices: DownloadChoice[] = [];
  for (const a of available) {
    if (!a.writable) continue;
    const loss = summarizeLoss(a.loss);
    // `blocking` is CONVERT's own verdict and is authoritative. The recompute
    // from severities is a belt-and-braces read of the same entries: a UI that
    // offered a download for an entry marked `error` would be handing over a
    // file the library said was wrong.
    const blocking = a.blocking || loss.some((l) => l.blocking);
    choices.push({
      format: a.format,
      kind: blocking ? 'refuse' : loss.length > 0 ? 'warn' : 'offer',
      loss,
    });
  }
  return choices;
}

/** The formats that had no writer, for a page that wants to say so once. */
export function unavailable(available: readonly Availability[]): readonly WriteFormat[] {
  return available.filter((a) => !a.writable).map((a) => a.format);
}

/**
 * One `<li>` per loss cause, CLONED FROM THE PAGE'S BANK.
 *
 * ⚠ THIS USED TO BE A TABLE OF SENTENCES IN THIS FILE. `LOSS_COPY` held 21
 * strings here while `LOSS_SENTENCES` in `macros/states.html` held the same
 * ones for the pair pages — one sentence with two homes, which is exactly what
 * the copy system exists to prevent, and I built the copy system. The Jinja one
 * is the correct home because every user-visible sentence on this site is
 * authored once there; this side clones, the way every other client-rendered
 * state already did.
 *
 * The COUNT is the only thing this surface adds. The bank's `<li>` carries a
 * `data-optional="count"` span, so `fill()` drops it when no count is supplied
 * and the line degrades to the pair page's wording rather than to
 * "Colours () — this format has no colour".
 *
 * A code with no entry in the bank renders as the raw code. `clip-dropped (3)`
 * is ugly and honest; a silently shorter list lies about what the file lost.
 */
function lossItems(loss: readonly LossSummary[], bank: ParentNode | null): HTMLLIElement[] {
  return loss.map((summary) => {
    const template = bank?.querySelector<HTMLElement>(
      `[data-loss-code="${CSS.escape(summary.code)}"]`,
    );
    const li = document.createElement('li');
    if (!template) {
      // Not silence, and not a thrown error either: the vocabulary belongs to
      // the feature engines and they will add to it faster than this bank.
      li.textContent = `${summary.code} (${summary.count})`;
      return li;
    }
    li.replaceChildren(...[...template.cloneNode(true).childNodes]);
    fillCount(li, summary.count);
    return li;
  });
}

/** Fill the one slot this surface owns, or remove it. */
function fillCount(root: ParentNode, count: number): void {
  const optional = root.querySelector<HTMLElement>('[data-optional="count"]');
  if (count <= 0) {
    optional?.remove();
    return;
  }
  const slot = root.querySelector<HTMLElement>('[data-slot="count"]');
  if (slot) slot.textContent = count.toLocaleString();
}

/** The bank a page rendered, or null on a page that did not include it. */
function lossBank(): ParentNode | null {
  const tpl = document.querySelector<HTMLTemplateElement>('template[data-loss-bank]');
  return tpl ? tpl.content : null;
}

/* ── painting the three outcomes ──────────────────────────────────────── */

/**
 * Render one format's outcome into a container.
 *
 * The two lossy outcomes must LOOK different, not merely read differently.
 * Three things separate them and only one is colour:
 *
 *   - `refuse` has NO download control in its subtree. That is the difference
 *     a user acts on, and it is structural rather than presentational, so it
 *     survives a stylesheet failing to load.
 *   - The banner variants differ (`--warn` vs `--error`), which is the colour
 *     part and is the part that carries no information on its own.
 *   - Each carries a left rule of a different weight and a different leading
 *     glyph, so the two are distinguishable in greyscale, at 200% zoom, and by
 *     a reader with any form of colour blindness. `docs/TECHNICAL_REQUIREMENTS`
 *     forbids colour as the sole carrier of state, and two red-ish banners a
 *     shade apart is exactly that with extra steps.
 *
 * Returns false when the page did not bank the template — reported by
 * `renderState`, and the caller keeps the plain button rather than showing
 * nothing.
 */
/** The loss codes a text encoding causes, as opposed to a format. */
const ENCODING_LOSS: ReadonlySet<LossCode> = new Set<LossCode>([
  'characters-escaped',
  'characters-dropped',
]);

/**
 * Which closing line a warning ends on. "Convert to a richer format" is the wrong fix when the
 * only cost is characters EUC-KR can't hold: the same format saved as UTF-8 keeps them. A PDF's
 * dropped emoji is the same code under UTF-8, and there the format IS the limit, so the encoding
 * line needs the write to have been EUC-KR as well.
 */
export function adviceFor(
  loss: readonly LossSummary[],
  encoding: WriteOptions['encoding'] = 'utf-8',
): 'format' | 'encoding' {
  return encoding !== 'utf-8' && loss.length > 0 && loss.every((s) => ENCODING_LOSS.has(s.code))
    ? 'encoding'
    : 'format';
}

export function renderOutcome(
  container: HTMLElement,
  choice: DownloadChoice,
  formatLabel: string,
  encoding: WriteOptions['encoding'] = 'utf-8',
): boolean {
  if (choice.kind === 'offer') {
    container.replaceChildren();
    return true;
  }
  const name = choice.kind === 'refuse' ? 'DOWNLOAD_REFUSED' : 'DOWNLOAD_LOSSY';
  // The codes go in as the `loss` list so the container SURVIVES `renderState`:
  // its filler removes any `data-slot-list` it is given no items for, which is
  // right for an empty list and was wrong here. Passing nothing meant the <ul>
  // was gone before the query below ran, so every warning and refusal shipped
  // without the list of what was lost — the one part of it that is specific.
  const frag = renderState(
    name,
    { format_label: formatLabel, format_label_2: formatLabel },
    { loss: choice.loss.map((summary) => summary.code) },
  );
  if (!frag) return false;
  // The list container is authored copy; its ITEMS are cloned from the bank, so
  // no sentence is assembled here. The raw codes `renderState` put in the list
  // are placeholders and are replaced wholesale: these lines are not plain
  // strings, they are marked-up lines with a slot.
  const list = frag.querySelector<HTMLElement>('[data-slot-list="loss"]');
  if (list) {
    const items = lossItems(choice.loss, lossBank());
    if (items.length === 0) list.remove();
    else list.replaceChildren(...items);
  }
  const advice = adviceFor(choice.loss, encoding);
  for (const line of frag.querySelectorAll<HTMLElement>('[data-advice]')) {
    if (line.dataset['advice'] !== advice) line.remove();
  }
  container.replaceChildren(frag);
  return true;
}

/**
 * Whether the download control may exist at all.
 *
 * Separate from `renderOutcome` and checked by the page, because "the button is
 * hidden" and "the button is absent" are different and only the second is
 * honest. A disabled button invites a user to hunt for the thing that enables
 * it; there is nothing to find.
 */
export function mayDownload(choice: DownloadChoice): boolean {
  return choice.kind !== 'refuse';
}
